import { QueryClient } from "@tanstack/react-query";

/**
 * Default cache behaviour shared by every query in the app.
 *
 * These values are deliberately conservative for a template — tune them per
 * project (or per query) once you know the data's freshness requirements:
 *
 * - `staleTime` — how long a fetched result is considered fresh. While fresh,
 *   re-mounting a component or refocusing the tab reuses the cache instead of
 *   refetching. 30s is a sensible floor that already kills most duplicate
 *   requests without serving badly stale data.
 * - `gcTime` — how long an unused (no observer) cache entry is kept before it
 *   is garbage-collected. Keeps back/forward navigation instant.
 * - `refetchOnWindowFocus` — refetch stale queries when the tab regains focus.
 *   This natively replaces the visibility-based refresh that `useAutoRefresh`
 *   did by hand (see frontend/AGENTS.md → "Server state vs client state").
 * - `retry` — one silent retry on transient failures. Auth (401) is already
 *   handled one layer down by `appFetch`, so this only covers flaky network.
 */
const DEFAULT_STALE_TIME_MS = 30_000;
const DEFAULT_GC_TIME_MS = 5 * 60_000;
const DEFAULT_QUERY_RETRY = 1;

/**
 * Builds a fresh {@link QueryClient}. Used by {@link queryClient} for the app
 * and by tests, which must create an isolated client per test so caches never
 * leak between cases.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        gcTime: DEFAULT_GC_TIME_MS,
        retry: DEFAULT_QUERY_RETRY,
        refetchOnWindowFocus: true,
      },
      mutations: {
        // Mutations are not idempotent — never auto-retry them.
        retry: 0,
      },
    },
  });
}

/**
 * The single app-wide query cache. Provided once at the root via
 * `QueryClientProvider` in `App.tsx`. Import this only for cache operations
 * outside React (rare); inside components, use the hooks in `src/queries/*`.
 */
export const queryClient = createQueryClient();
